import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import Switch from '@material-ui/core/Switch';
import Skeleton from '@material-ui/lab/Skeleton';

import Accordion from '@material-ui/core/Accordion'
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';

import { paper, divider } from 'app/styles/core';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    divider: {
        ...divider(theme)
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    marginTopLarge: {
        marginTop: theme.spacing(4),
    },
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
    panelHeading: {
        fontWeight: '600',
        flexBasis: '33.33%',
        flexShrink: 0,
    },
    panelSecondaryHeading: {
        fontSize: theme.typography.pxToRem(15),
        color: theme.palette.text.secondary,
    },
    textSuccess: {
        color: theme.palette.success.main,
        display: 'block'
    },
    textError: {
        color: theme.palette.error.main
    },
    switchContainer: {
        paddingLeft: 4,
        marginBottom: theme.spacing(1)
    },
    button: {
        textAlign: 'left',
        justifyContent: 'flex-start'
    },
    buttonPadding: {
        paddingRight: 54
    }
}))

const ClubPanel = props => {

    const { app } = useApp();
    const api = useApi();

    const { getAssociations, membership, classes } = props;

    const [ settings, setSettings ] = useState(membership.meta);
    const [ expanded, setExpanded ] = useState(false);
    const [ hasChanged, setHasChanged ] = useState(false);

    const [ resignMembershipDialogOpen, setResignMembershipDialogOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);

    const handleExpanded = () => setExpanded(!expanded);

    const hasCard = (app.user.card_brand != "") ? true : false;

    const handleChange = event => {
        setHasChanged(true);
        setSettings({ ...settings, [event.target.name]: event.target.checked });
    };

    const handleSnailMailTo = event => {
        setHasChanged(true);
        setSettings({ ...settings, [event.target.name]: event.target.value });
    };

    const isClubVerified = membership.club_verified_at !== null;

    const getSecondaryColor = () => {
        if (membership.meta.member_resigned_at != null) return 'error';
        if (membership.club.id === app.user.active_club_id) return 'primary';
        if (isClubVerified) return 'primary';
        return 'error';
    }

    const getSecondaryText = () => {
        if (membership.meta.member_resigned_at) return 'Active - Pending Resignation';
        if (membership.club.id === app.user.active_club_id) return 'Active - Current Organization';
        if (isClubVerified) return 'Active';
        return 'Requested - Pending Review';
    }

    const inGoodStanding = membership.status.in_good_standing;

    const getMembershipStatusHeader = inGoodStanding ? "In Good Standing" : "Payment Due";

    const getMembershipStatusColor = inGoodStanding ? classes.textSuccess : classes.textError;

    const isMember = membership.user.membership_level ? membership.user.membership_level.members_level : false

    const displayMDSettings = (isMember || (membership.user.membership_level && membership.user.membership_level.public))

    const postSettings = () => {
        setLoading(true);

        const data = settings;

        api.post('/profile/association/' + membership.id + '/settings', data).finally(() => setLoading(false));
    }

    const verifyResignMembership = membership => {
        setResignMembershipDialogOpen(true);
    }

    const postResign = () => {
        setLoading(true);

        api.post('/profile/association/' + membership.id + '/resign').then(() => {

            closeResignMembershipDialog(true);
            getAssociations();

        }).finally(() => setLoading(false));
    }

    const postWithdrawAccessRequest = () => {
        setLoading(true);

        api.post('/profile/association/' + membership.id + '/withdraw-request').then(() => getAssociations()).finally(() => setLoading(false));
    }

    const closeResignMembershipDialog = () => {
        setResignMembershipDialogOpen(false);
    }

    return (
        <Fragment>
            <Accordion
                expanded={expanded}
                onChange={handleExpanded}
                >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={('panel' + membership.id + '-content')}
                    id={('panel' + membership.id + '-header')}
                    >
                    <Grid container alignItems='center' spacing={3}>
                        <Grid item xs={6} sm={4} md={6} lg={4}>
                            <Typography variant='subtitle1' className={classes.panelHeading}>
                                {membership.club.name}
                            </Typography>
                            {isClubVerified &&
                                <Typography variant="subtitle2" color="textSecondary">
                                    {membership.club.groups[0].member_called}
                                </Typography>
                            }
                        </Grid>
                        <Grid item xs={6} sm={8} md={6} lg={8}>
                            <Typography color={getSecondaryColor()}>{getSecondaryText()}</Typography>
                            {isClubVerified &&
                                <Typography variant="subtitle2" className={getMembershipStatusColor}>
                                    {getMembershipStatusHeader}
                                </Typography>
                            }
                        </Grid>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Loading isLoading={loading}/>
                    {(isClubVerified) ? (
                        <Grid container spacing={3}>
                            <Grid item sm={7} xs={12} md={12} lg={7}>
                                <Typography gutterBottom variant="subtitle1">
                                    Settings
                                </Typography>
                                <Divider className={classes.divider}/>
                                <Typography gutterBottom variant="subtitle2" color="textSecondary">
                                    Communication
                                </Typography>
                                <div className={classes.switchContainer}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.receive_emails}
                                                onChange={handleChange}
                                                name="receive_emails"
                                                color="primary"
                                            />
                                        }
                                        label="Receive emails from this organization"
                                        margin="normal"
                                    />
                                </div>
                                <div className={classes.switchContainer}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.receive_sms}
                                                onChange={handleChange}
                                                name="receive_sms"
                                                color="primary"
                                            />
                                        }
                                        label="Receive text messages from this organization"
                                        margin="normal"
                                    />
                                </div>
                                <Typography gutterBottom variant="subtitle2" color="textSecondary" className={classes.marginTop}>
                                    Preferred Mailing Address
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup aria-label="gender" name="snailmail_to" value={settings.snailmail_to} onChange={handleSnailMailTo}>
                                        <FormControlLabel value="home" control={<Radio size="small" color="primary"/>} label="Home Address" />
                                        <FormControlLabel value="work" control={<Radio size="small" color="primary"/>} label="Business Address" />
                                    </RadioGroup>
                                </FormControl>
                                {displayMDSettings &&
                                    <Fragment>
                                        <Typography gutterBottom variant="subtitle2" color="textSecondary" className={classes.marginTop}>
                                            Directory
                                        </Typography>
                                        <div className={classes.switchContainer}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.display_contact}
                                                        onChange={handleChange}
                                                        name="display_contact"
                                                        color="primary"
                                                    />
                                                }
                                                label="Display Home Contact Information"
                                                margin="normal"
                                            />
                                        </div>
                                        <div className={classes.switchContainer}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.display_home_address}
                                                        onChange={handleChange}
                                                        name="display_home_address"
                                                        color="primary"
                                                    />
                                                }
                                                label="Display Home Address"
                                                margin="normal"
                                            />
                                        </div>
                                        <div className={classes.switchContainer}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.display_work}
                                                        onChange={handleChange}
                                                        name="display_work"
                                                        color="primary"
                                                    />
                                                }
                                                label="Display Work Information"
                                                margin="normal"
                                            />
                                        </div>
                                        <div className={classes.switchContainer}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.display_work_contact}
                                                        onChange={handleChange}
                                                        name="display_work_contact"
                                                        color="primary"
                                                    />
                                                }
                                                label="Display Work Contact Information"
                                                margin="normal"
                                            />
                                        </div>
                                        <div className={classes.switchContainer}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.display_work_address}
                                                        onChange={handleChange}
                                                        name="display_work_address"
                                                        color="primary"
                                                    />
                                                }
                                                label="Display Work Address"
                                                margin="normal"
                                            />
                                        </div>
                                        <div className={classes.switchContainer}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.display_spouse}
                                                        onChange={handleChange}
                                                        name="display_spouse"
                                                        color="primary"
                                                    />
                                                }
                                                label="Display Spouse Information"
                                                margin="normal"
                                            />
                                        </div>
                                    </Fragment>
                                }
                                <Typography gutterBottom variant="subtitle2" color="textSecondary" className={classes.marginTop}>
                                    Other
                                </Typography>
                                <div className={classes.switchContainer}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.auto_renew}
                                                onChange={handleChange}
                                                name="auto_renew"
                                                color="primary"
                                            />
                                        }
                                        label="Automaticly pay your membership dues"
                                        margin="normal"
                                    />
                                    {!hasCard &&
                                        <FormHelperText>Must have a saved credit card</FormHelperText>
                                    }
                                </div>
                                <div className={classes.switchContainer}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.admin_profile_access}
                                                onChange={handleChange}
                                                name="admin_profile_access"
                                                color="primary"
                                            />
                                        }
                                        label="Allow administrators from this organization to update your profile, membership, payment and security"
                                        margin="normal"
                                    />
                                </div>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    disabled={!hasChanged}
                                    className={classes.marginTopLarge}
                                    onClick={postSettings}
                                    >
                                    Update Settings
                                </Button>
                            </Grid>

                            <Grid item xs={12} sm={5} md={12} lg={5}>
                                <Typography gutterBottom variant="subtitle1">
                                    About Your Membership
                                </Typography>
                                <Divider className={classes.divider}/>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Membership Level
                                </Typography>
                                <Typography className={classes.marginBottom} variant="body2">
                                    {membership.club.groups[0].title}
                                </Typography>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Organization Description
                                </Typography>
                                <Typography gutterBottom variant="body2">
                                    {membership.club.description}
                                </Typography>
                                <div className={classes.marginTopLarge}/>
                                <Typography gutterBottom variant="subtitle1">Actions</Typography>
                                <Divider className={classes.divider}/>
                                <Button
                                    className={classes.button}
                                    color="primary"
                                    fullWidth
                                    size="small"
                                    href={membership.club.url}
                                    >
                                    Visit Dashboard
                                </Button>
                                <Button
                                    className={classes.button}
                                    color="primary"
                                    fullWidth
                                    size="small"
                                    onClick={() => verifyResignMembership(membership)}
                                    disabled={Boolean(membership.meta.member_resigned_at)}
                                    >
                                    {isMember ? "Resign Membership" : "Remove Association"}
                                </Button>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container alignItems='center' spacing={3} className={classes.buttonPadding}>
                            <Grid item xs={6} sm={4} md={6} lg={4}/>
                            <Grid item xs={6} sm={8} md={6} lg={8}>
                                <RedButton
                                    className={classes.button}
                                    color="primary"
                                    onClick={postWithdrawAccessRequest}
                                    disabled={loading}
                                    variant="outlined"
                                    >
                                    Withdraw Access Request
                                </RedButton>
                            </Grid>
                        </Grid>
                    )}
                </AccordionDetails>
            </Accordion>
            <Dialog
                open={resignMembershipDialogOpen}
                onClose={closeResignMembershipDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                >
                <DialogTitle id="alert-dialog-title">
                    {isMember ? "Resign your Membership?" : "Remove Association?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to {isMember ? "resign your membership from" : "remove your association with"} {membership.club.name}? {isMember && 'While administrators will be notified of your decision, generally you will need to submit a formal resignation.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeResignMembershipDialog} color="primary" autoFocus>
                        Cancel
                    </Button>
                    <RedButton onClick={postResign} variant="contained">
                        {isMember ? "Resign Membership" : "Remove Association"}
                    </RedButton>
                </DialogActions>
            </Dialog>
        </Fragment>
    )
}

export default function Associations() {

    const classes = useStyles();
    const api = useApi();

    const [ associations, setAssociations ] = useState([]);
    const [ loading, setLoading ] = useState(false);

    // Getter functions ----------------------------------------

    const getAssociations = () => {
        setLoading(true);

        api.get('/profile/associations')
            .then(res => {
                console.log(res)
                setAssociations(res.associations)})
            .then(() => setLoading(false));
    }

    useEffect(() => {
        getAssociations();
    }, []);

    const activeClubs = associations.length ? associations.filter(m => m.club_verified_at !== null) : [];
    const pendingClubs = associations.length ? associations.filter(m => m.club_verified_at === null) : [];

    return (
        <form noValidate autoComplete="off">
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Organizations
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary" gutterBottom>
                    View your active and pending organization associations as well as adjust settings for each.
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                    <Typography color="primary" variant="inherit" display="inline">
                        {activeClubs.length} Active Organization{activeClubs.length == 1 ? '': 's'}
                    </Typography>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    <Typography color="error" variant="inherit" display="inline">
                        {pendingClubs.length} Pending Organization{pendingClubs.length == 1 ? '': 's'}
                    </Typography>
                </Typography>
            </Paper>
            { loading ? (
                <Paper className={classes.paper}>
                    <Grid container alignItems='center' spacing={3}>
                        <Grid item xs={6} sm={4} md={6} lg={4}>
                            <Skeleton animation="wave" height={18} width="25%" style={{ marginBottom: 6 }} />
                            <Skeleton animation="wave" height={14} width="15%" />
                        </Grid>
                        <Grid item xs={6} sm={8} md={6} lg={8}>
                            <Skeleton animation="wave" height={18} width="25%" style={{ marginBottom: 6 }} />
                            <Skeleton animation="wave" height={14} width="15%" />
                        </Grid>
                    </Grid>
                </Paper>
            ) : associations.map(m => (
                <ClubPanel
                    key={m.id}
                    membership={m}
                    classes={classes}
                    getAssociations={getAssociations}
                />
            ))}

        </form>
    )
}
